import { Injectable } from '@angular/core';
import { VideoDetailsModel, VoteEntryResponseModel, VoteStatusResponseModel, VotingActivityLogRequestModel, VotingActivityLogResponseModel, VotingResponseModel } from './voting.model';
import { firstValueFrom } from 'rxjs';
import { APIErrorModel, BaseResponse } from '../../shared/models/base-response.model';
import { HttpResult } from '../../core/services/base-http.service';
import { VotingApiService } from './voting-api.service';
import { StorageService } from '../../core/services/storage.service';

@Injectable({
  providedIn: 'root'
})
export class VotingBllService {

  constructor(
    private api: VotingApiService,
    private storage: StorageService
  ) { }

  /**
   * Fetch the list of contest participants
   */
  async fetchParticipantsList(): Promise<BaseResponse<VotingResponseModel>> {
    const result: HttpResult<BaseResponse<VotingResponseModel>> =
      await firstValueFrom(this.api.getContestParticipants());

    const { status, body } = result;
    if (status === 200) {
      return body;
    } else {
      throw <APIErrorModel>{
        status,
        message: body.message,
        data: body.data,
      };
    }
  }

  /**
   * Fetch vote status of the user.
   */
  async fetchVoteStatus(): Promise<BaseResponse<VoteStatusResponseModel>> {
    const result: HttpResult<BaseResponse<VoteStatusResponseModel>> =
      await firstValueFrom(this.api.getVoteStatus());

    const { status, body } = result;
    if (status === 200) {
      return body;
    } else {
      throw <APIErrorModel>{
        status,
        message: body.message,
        data: body.data,
      };
    }
  }

  /**
   * Submit a vote for a participant entry.
   * @param encoded_participant_id The encoded ID of the participant to vote for.
   * @returns Promise resolving to the vote entry response with updated vote counts after voting.
   */
  async fetchVoteEntry(encoded_participant_id: string): Promise<BaseResponse<VoteEntryResponseModel>> {
    const result: HttpResult<BaseResponse<VoteEntryResponseModel>> =
      await firstValueFrom(this.api.voteEntry(encoded_participant_id));

    const { status, body } = result;
    if (status === 200) {
      return body;
    } else {
      throw <APIErrorModel>{
        status,
        message: body.message,
        data: body.data,
      };
    }
  }

  /**
   * unvote entry
   * @param participant_id The encoded ID of the participant to unvote.
   * @returns Promise resolving to vote entry response with updated vote counts after unvoting  
   */
  async fetchUnVoteEntry(encoded_participant_id: string): Promise<BaseResponse<VoteEntryResponseModel>> {
    const result: HttpResult<BaseResponse<VoteEntryResponseModel>> =
      await firstValueFrom(this.api.unVoteEntry(encoded_participant_id));

    const { status, body } = result;
    if (status === 200) {
      return body;
    } else {
      throw <APIErrorModel>{
        status,
        message: body.message,
        data: body.data,
      };
    }
  }

  /**
   * save video details
   * @param videoDetails 
   */
  saveVideoDetails(videoDetails: VideoDetailsModel) {
    this.storage.saveSelectedVideoDetails(videoDetails);
  }

  /**
   * get saved video details
   * @returns The saved video details object from local storage, or null if not found.
   */
  getSavedVideoDetails(): VideoDetailsModel | null {
    return this.storage.getSelectedVideoDetails();
  }


  /**
   * @description Submits a contest activity log for the current event.
   * @returns Promise resolving to voting activity log response after adding log.
   */
  async submitContestActivityLog(payload: VotingActivityLogRequestModel): Promise<BaseResponse<VotingActivityLogResponseModel>> {
    const result: HttpResult<BaseResponse<VotingActivityLogResponseModel>> =
      await firstValueFrom(this.api.addContestActivityLog(payload));

    const { status, body } = result;
    if (status === 200) {
      return body;
    } else {
      throw <APIErrorModel>{
        status,
        message: body.message,
        data: body.data,
      };
    }
  }

}
